/**
 * Login container (ADR-03 §2.3 steps 1–3, D-036, D-038): password → second factor | first-login
 * TOTP enrolment → `onAuthenticated`. The `Login` screen is presentational; this file owns the
 * step state and the calls. A page reload mid-login resumes at the pending step from the session
 * (`state: password_ok`). The preview banner comes from `GET /office` (D-223: on every screen).
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Login, type LoginStep, type PreviewBannerData } from "@faylasuf/ui";
import type { EnrolmentData, SessionInfo } from "../api/types.js";
import { useApp } from "../app/context.js";
import { errorMessage } from "../app/errors.js";
import { isApiClientError } from "../api/client.js";

/** [C30] D-283: the session cookie set at the password step never came back. */
const isNoCookie = (e: unknown): boolean => isApiClientError(e) && e.code === "SESSION_REQUIRED" && e.params["reason"] === "no_cookie";
import { keys } from "../app/query.js";

export interface LoginPageProps {
  readonly pendingSession?: SessionInfo | null;
  readonly initialError?: unknown;
  readonly onAuthenticated: () => void;
}

class LoginNotEntered extends Error {
  constructor(readonly state: string) {
    super("LOGIN_NOT_ENTERED");
  }
}

export function LoginPage({ pendingSession = null, initialError, onAuthenticated }: LoginPageProps) {
  const { source, t } = useApp();
  const pendingStep: LoginStep | null =
    pendingSession?.session.state === "password_ok" ? (pendingSession.user.twoFactorEnrolled ? "second_factor" : "enrol_second_factor") : null;

  const [step, setStep] = useState<LoginStep>(pendingStep ?? "credentials");
  const [username, setUsername] = useState(pendingSession?.user.username ?? "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [enrolment, setEnrolment] = useState<EnrolmentData | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[] | null>(null);
  const [error, setError] = useState<string | null>(initialError ? errorMessage(t, initialError) : null);
  const [busy, setBusy] = useState(false);

  const office = useQuery({ queryKey: keys.office, queryFn: () => source.office(), retry: false, staleTime: Infinity });
  const banner: PreviewBannerData | null =
    pendingSession?.preview ? { text: t("preview.banner"), wipeDate: pendingSession.preview.wipeDate ?? t("common.dash") }
    : office.data?.preview ? { text: t("preview.banner"), wipeDate: office.data.preview.wipeDate ?? t("common.dash") }
    : null;

  // Enrolment step: fetch the secret as soon as the step is shown (D-036).
  useEffect(() => {
    if (step !== "enrol_second_factor" || enrolment || recoveryCodes) return;
    let cancelled = false;
    source
      .totpEnrol()
      .then((e) => {
        if (!cancelled) setEnrolment(e);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // [C30 review] the first request of the enrolment step is this one: a lost cookie shows here first (D-283)
        if (isNoCookie(e)) {
          setError(t("login.cookie_missing"));
          setStep("credentials");
          setCode("");
        } else setError(errorMessage(t, e));
      });
    return () => {
      cancelled = true;
    };
  }, [step, enrolment, recoveryCodes, source, t]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      // [C30] O-12, D-283: the cookie set at the password step never came back → say why, start over
      if (isNoCookie(e) && step !== "credentials") {
        setError(t("login.cookie_missing"));
        setStep("credentials");
        setCode("");
        setEnrolment(null);
      } else if (e instanceof LoginNotEntered) setError(t("login.not_entered", { state: e.state }));
      else setError(errorMessage(t, e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * [C37] F-372: after a successful sign-in step the session must now be active. If the next request fails or the
   * server still reports another state, the page says so (with the state) instead of staying silently on the step.
   */
  const enter = async () => {
    const s = await source.session();
    if (s.session.state !== "active") throw new LoginNotEntered(s.session.state);
    onAuthenticated();
  };

  const submit = () => {
    if (step === "credentials") {
      void run(async () => {
        const r = await source.login(username.trim().toLowerCase(), password);
        setPassword("");
        if (r.step === "active") await enter();
        else setStep(r.step);
      });
    } else if (step === "second_factor") {
      void run(async () => {
        await source.secondFactor(code);
        setCode("");
        await enter();
      });
    } else {
      void run(async () => {
        const r = await source.totpConfirm(code);
        setCode("");
        setRecoveryCodes(r.recoveryCodes);
      });
    }
  };

  const back = () => {
    // Abandon the half-open session (state password_ok) and start over.
    void source.logout().catch(() => undefined);
    setStep("credentials");
    setCode("");
    setEnrolment(null);
    setRecoveryCodes(null);
    setError(null);
  };

  return (
    <Login
      step={step}
      username={username}
      password={password}
      code={code}
      onUsernameChange={setUsername}
      onPasswordChange={setPassword}
      onCodeChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))}
      onSubmit={submit}
      {...(step !== "credentials" ? { onBack: back } : {})}
      enrolment={enrolment}
      recoveryCodes={recoveryCodes}
      onContinue={() => void run(enter)}
      error={error}
      busy={busy}
      banner={banner}
    />
  );
}
